import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input, Badge, Select, LoadingSpinner, ErrorSummary, Pagination } from '@/components/common';
import { PlusIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon, EyeIcon, CubeIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '@/services/api';
import { ImportExportControls } from '@/components/admin/ImportExportControls';
import { useBulkSelection, BulkActionBar, BulkSelectAllCheckbox, BulkSelectCheckbox } from '@/components/bulk';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import type { PaginatedResponse } from '@/types';
import toast from 'react-hot-toast';

/**
 * Products list -- item 10 of the 2026-09-18 batch.
 *
 * Create/Edit moved off this page entirely (`ProductFormPage`, full-page with
 * back navigation, per the ask); a row click opens `ProductDetailPage`. What
 * is left here: search, the four inline filters (Type/Group/Subgroup/Status --
 * all four already in `ProductViewSet.filterset_fields`, so this is wiring,
 * not new backend work), checkboxes, bulk delete, and bulk status change.
 *
 * Bulk status is its OWN control (`bulkStatusValue` + Apply), not the shared
 * `BulkActionBar`'s Activate/Deactivate: `Product` has no `is_active` field --
 * it has a three-way `status` -- so those two buttons would call the generic
 * `POST bulk/` and get back `not_supported` for every row (see
 * `core.bulk_actions.BulkActionMixin.bulk_active_field`). `allowStatusChange`
 * is turned off on the shared bar for that reason and delete is left on
 * (Product's soft delete is the same guarded delete every other bulk screen
 * gets). The status control instead calls this app's own
 * `POST /products/products/bulk-status/` (`ProductViewSet.bulk_status`).
 */

interface Lookup {
  id: string;
  name: string;
}

interface AssetBrief {
  id: string;
  src: string;
  width: number | null;
  height: number | null;
  alt_text: string;
}

interface Product {
  id: string;
  name: string;
  code: string;
  insurance_type?: string;
  type_name?: string;
  group?: string;
  group_name?: string;
  subgroup?: string;
  subgroup_name?: string;
  policy_type?: string;
  policy_type_name?: string;
  status: string;
  thumbnail_image?: AssetBrief | null;
}

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'discontinued', label: 'Discontinued' },
];

const statusVariant = (status: string): 'success' | 'warning' | 'default' | 'error' => {
  switch (status) {
    case 'active':
      return 'success';
    case 'draft':
      return 'default';
    case 'discontinued':
      return 'error';
    default:
      return 'warning';
  }
};

const itemsPerPage = 20;

export function ProductsListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const { term: search, debouncedTerm: debouncedSearch, setTerm: setSearch } = useDebouncedSearch();
  const [typeFilter, setTypeFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [subgroupFilter, setSubgroupFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [bulkStatusValue, setBulkStatusValue] = useState('active');

  const resetToFirstPage = () => setPage(1);

  const { data: types = [] } = useQuery<Lookup[]>({
    queryKey: ['insurance-types-lookup'],
    queryFn: async () => {
      const res = await api.get('/products/types/', { params: { page_size: 200 } });
      return res.data.results || res.data;
    },
  });

  const { data: groups = [] } = useQuery<Lookup[]>({
    queryKey: ['insurance-groups-lookup-for-product-filter'],
    queryFn: async () => {
      const res = await api.get('/products/groups/', { params: { page_size: 200 } });
      return res.data.results || res.data;
    },
  });

  const { data: subgroups = [] } = useQuery<Lookup[]>({
    queryKey: ['insurance-subgroups-lookup-for-product-filter'],
    queryFn: async () => {
      const res = await api.get('/products/subgroups/', { params: { page_size: 200 } });
      return res.data.results || res.data;
    },
  });

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery<PaginatedResponse<Product>>({
    queryKey: ['admin-products', page, debouncedSearch, typeFilter, groupFilter, subgroupFilter, statusFilter],
    queryFn: async () => {
      const params: Record<string, string> = { page: String(page), page_size: String(itemsPerPage) };
      if (debouncedSearch) params.search = debouncedSearch;
      if (typeFilter) params.insurance_type = typeFilter;
      if (groupFilter) params.group = groupFilter;
      if (subgroupFilter) params.subgroup = subgroupFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await api.get('/products/products/', { params });
      return res.data;
    },
  });

  const products = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const visibleIds = products.map((p) => p.id);
  const bulkSelection = useBulkSelection(visibleIds);

  const bulkStatusMutation = useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: string }) =>
      (await api.post('/products/products/bulk-status/', { ids, status })).data as {
        succeeded: string[];
        failed: Array<{ id: string; detail: string }>;
        detail: string;
      },
    onSuccess: (result) => {
      // Same rule as the shared BulkActionBar: a partial failure is never
      // reported as a plain success, and the selection for the rows that did
      // NOT change is kept so the person can see and retry them.
      if (result.failed.length === 0) {
        toast.success(result.detail);
        bulkSelection.clear();
      } else {
        toast(result.detail, { icon: result.succeeded.length ? '⚠️' : '❌' });
      }
      if (result.succeeded.length) {
        queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      }
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const typeOptions = types.map((t) => ({ value: t.id, label: t.name }));
  const groupOptions = groups.map((g) => ({ value: g.id, label: g.name }));
  const subgroupOptions = subgroups.map((s) => ({ value: s.id, label: s.name }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Products</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Master catalog of insurance products
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ImportExportControls apiEndpoint="/products/products/" invalidateKeys={['admin-products']} />
          <Button
            variant="primary"
            leftIcon={<PlusIcon className="w-4 h-4" />}
            onClick={() => navigate('/admin/products/new')}
          >
            Add Product
          </Button>
        </div>
      </div>

      <Card padding="md">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <div className="sm:col-span-2">
            <Input
              placeholder="Search products..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                resetToFirstPage();
              }}
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            />
          </div>
          <Select
            options={[{ value: '', label: 'All Types' }, ...typeOptions]}
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              resetToFirstPage();
            }}
          />
          <Select
            options={[{ value: '', label: 'All Groups' }, ...groupOptions]}
            value={groupFilter}
            onChange={(e) => {
              setGroupFilter(e.target.value);
              resetToFirstPage();
            }}
          />
          <Select
            options={[{ value: '', label: 'All Subgroups' }, ...subgroupOptions]}
            value={subgroupFilter}
            onChange={(e) => {
              setSubgroupFilter(e.target.value);
              resetToFirstPage();
            }}
          />
          <Select
            options={[{ value: '', label: 'All Statuses' }, ...STATUS_OPTIONS]}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              resetToFirstPage();
            }}
          />
        </div>
      </Card>

      {bulkSelection.count > 0 && (
        <div className="space-y-2">
          <BulkActionBar
            resource="products/products"
            selectedIds={bulkSelection.selectedIds}
            entityName="products"
            onChanged={() => queryClient.invalidateQueries({ queryKey: ['admin-products'] })}
            onClear={bulkSelection.clear}
            allowStatusChange={false}
          />
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 dark:border-primary-800 dark:bg-primary-900/40">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Set status:</span>
            {/* `Select`'s own wrapper div is `w-full`; a plain width class on
                the component would fight that inside this flex row, so the
                width goes on a wrapping div instead. */}
            <div className="w-40">
              <Select
                options={STATUS_OPTIONS}
                value={bulkStatusValue}
                onChange={(e) => setBulkStatusValue(e.target.value)}
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              isLoading={bulkStatusMutation.isPending}
              onClick={() => bulkStatusMutation.mutate({ ids: bulkSelection.selectedIds, status: bulkStatusValue })}
            >
              Apply
            </Button>
          </div>
        </div>
      )}

      {isError && <ErrorSummary messages={[getErrorMessage(error)]} />}

      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : isError ? (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Could not load products</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Try again in a moment.</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <CubeIcon className="w-12 h-12 mx-auto text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No products found</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Get started by adding your first product.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="w-10 px-4 py-3">
                      <BulkSelectAllCheckbox
                        checked={bulkSelection.allVisibleSelected}
                        indeterminate={bulkSelection.someVisibleSelected}
                        onChange={bulkSelection.toggleAllVisible}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Group</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Subgroup</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {products.map((product) => (
                    <tr
                      key={product.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                      onClick={() => navigate(`/admin/products/${product.id}`)}
                    >
                      <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                        <BulkSelectCheckbox
                          checked={bulkSelection.isSelected(product.id)}
                          onChange={() => bulkSelection.toggle(product.id)}
                          label={`Select ${product.name}`}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {product.thumbnail_image?.src ? (
                            <img
                              src={product.thumbnail_image.src}
                              alt=""
                              width={40}
                              height={40}
                              loading="lazy"
                              decoding="async"
                              className="w-10 h-10 rounded-lg object-cover bg-gray-100 dark:bg-gray-700"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                              <CubeIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{product.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{product.code}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{product.type_name || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{product.group_name || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{product.subgroup_name || '-'}</td>
                      <td className="px-6 py-4">
                        <Badge variant={statusVariant(product.status)}>
                          {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
                        </Badge>
                      </td>
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            aria-label={`View ${product.name}`}
                            onClick={() => navigate(`/admin/products/${product.id}`)}
                          >
                            <EyeIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            aria-label={`Edit ${product.name}`}
                            onClick={() => navigate(`/admin/products/${product.id}/edit`)}
                          >
                            <PencilIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            aria-label={`Delete ${product.name}`}
                            onClick={async () => {
                              if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
                              try {
                                await api.delete(`/products/products/${product.id}/`);
                                toast.success('Product deleted');
                                queryClient.invalidateQueries({ queryKey: ['admin-products'] });
                              } catch (err) {
                                toast.error(getErrorMessage(err));
                              }
                            }}
                          >
                            <TrashIcon className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalCount}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default ProductsListPage;
